/**
 * HTTP client for vertex360-ai-service (Python).
 * Separate from Laravel apiCall — different base URL, no session logout on 401.
 */
import axios from "axios";
import { getToken } from "helpers/Tokens";
import { getJourneyHeaders } from "Utils/journeyHeaders";

const AI_STATUS_MESSAGES = {
  402: "Insufficient AI credits. Top up your balance to continue.",
  403: "This AI feature is not available for your organisation or role.",
  404: "The requested AI resource was not found.",
  503: "AI service is temporarily unavailable. Please try again shortly.",
};

/**
 * Resolve SP id for AI tenancy headers.
 * - sp_standard_user: selectedProvider (set at login / org switcher in sidebar)
 * - sp_admin: user.service_provider.id (selectedProvider is cleared at login)
 * - sp_manager / custom roles: user.sp_id (selectedProvider is cleared at login)
 */
const resolveSpId = () => {
  const cachedProvider = localStorage.getItem("selectedProvider");
  if (cachedProvider) {
    try {
      const parsed = JSON.parse(cachedProvider);
      const fromProvider = Array.isArray(parsed) ? parsed[0]?.value : parsed?.value;
      if (fromProvider) {
        return fromProvider;
      }
    } catch {
      // fall through to user object
    }
  }

  const cachedUser = localStorage.getItem("user");
  if (!cachedUser) {
    return undefined;
  }
  try {
    const user = JSON.parse(cachedUser);
    return (
      user?.sp_id ||
      user?.service_provider?.id ||
      user?.payment_setting?.sp_id ||
      undefined
    );
  } catch {
    return undefined;
  }
};

const extractAiErrorDetail = (data) => {
  if (!data) {
    return { message: "", code: "" };
  }
  if (typeof data === "string") {
    return { message: data, code: "" };
  }
  if (data.message) {
    return { message: data.message, code: data.code || "" };
  }
  const detail = data.detail;
  if (typeof detail === "string") {
    return { message: detail, code: "" };
  }
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return {
      message: detail.message || "",
      code: detail.code || "",
    };
  }
  return { message: "", code: "" };
};

export const parseAiError = (error) => {
  let message = "";
  let code = "";
  let status = 0;

  if (error.response) {
    status = error.response.status;
    const extracted = extractAiErrorDetail(error.response.data);
    message = extracted.message;
    code = extracted.code;
    if (!message) {
      message = AI_STATUS_MESSAGES[status] || "";
    }
    if (!message && status >= 400) {
      message = "Something went wrong with the AI request. Please try again.";
    }
  } else if (error.request) {
    message = "Unable to reach the AI service. Check your connection and try again.";
    code = "NETWORK_ERROR";
  } else {
    message = error.message || "Something went wrong with the AI request.";
  }

  const err = new Error(message || "Something went wrong with the AI request.");
  err.code = code;
  err.status = status;
  return err;
};

const buildHeaders = (token) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...getJourneyHeaders(),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const spId = resolveSpId();
  if (spId) {
    headers.sp_id = spId;
    headers["X-SP-ID"] = String(spId);
  }

  return headers;
};

const request = async (method, url, body = undefined, options = {}) => {
  const token = options.token ?? getToken();
  const config = {
    method,
    url,
    headers: buildHeaders(token),
    timeout: options.timeout ?? 120000,
  };

  if (body !== undefined) {
    config.data = body;
  }

  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    throw parseAiError(error);
  }
};

export const aiClient = {
  get: (url, options) => request("GET", url, undefined, options),
  post: (url, body, options) => request("POST", url, body, options),
  delete: (url, options) => request("DELETE", url, undefined, options),
};

/** 15 minutes — streamed batch analyse can run multiple LLM calls. */
export const AI_STREAM_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * POST + read SSE `data: {...}` lines until complete or error.
 * @returns {Promise<object>} complete event payload
 */
export const aiStreamPost = async (url, body, options = {}) => {
  const {
    onEvent,
    signal,
    timeout = AI_STREAM_TIMEOUT_MS,
    token = getToken(),
  } = options;

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeout);

  const abortFromSignal = () => timeoutController.abort();
  if (signal) {
    if (signal.aborted) {
      clearTimeout(timeoutId);
      throw parseAiError({ message: "Request cancelled" });
    }
    signal.addEventListener("abort", abortFromSignal);
  }

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        ...buildHeaders(token),
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      signal: timeoutController.signal,
    });
  } catch (error) {
    if (error?.name === "AbortError") {
      const err = new Error(
        signal?.aborted
          ? "AI analyse cancelled."
          : "AI analyse timed out. Please try again with fewer timesheets."
      );
      err.code = signal?.aborted ? "CANCELLED" : "TIMEOUT";
      throw err;
    }
    throw parseAiError({ request: true, message: error?.message });
  } finally {
    clearTimeout(timeoutId);
    if (signal) {
      signal.removeEventListener("abort", abortFromSignal);
    }
  }

  if (!response.ok) {
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    const extracted = extractAiErrorDetail(data);
    const err = new Error(
      extracted.message ||
        AI_STATUS_MESSAGES[response.status] ||
        "Something went wrong with the AI request. Please try again."
    );
    err.code = extracted.code;
    err.status = response.status;
    err.data = data?.detail?.data || data?.data;
    throw err;
  }

  if (!response.body) {
    throw parseAiError({ message: "Streaming is not supported in this browser." });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completeEvent = null;

  const handleEvent = (payload) => {
    if (payload?.type === "complete") {
      completeEvent = payload;
    }
    onEvent?.(payload);
    if (payload?.type === "error") {
      const err = new Error(payload.message || "AI analyse failed.");
      err.code = payload.code || "AI_ERROR";
      err.status = payload.status || 500;
      err.data = payload.data;
      throw err;
    }
  };

  const processChunk = (chunk) => {
    const lines = chunk.split("\n");
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;
      const raw = trimmed.slice(5).trim();
      if (!raw) continue;
      handleEvent(JSON.parse(raw));
      if (completeEvent) return true;
    }
    return false;
  };

  try {
    while (!completeEvent) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop() || "";

      for (const chunk of chunks) {
        if (processChunk(chunk)) break;
      }
    }

    if (!completeEvent && buffer.trim()) {
      processChunk(buffer);
    }
  } finally {
    if (completeEvent) {
      try {
        await reader.cancel();
      } catch {
        // Stream may already be closed by the server.
      }
    }
    reader.releaseLock?.();
  }

  if (!completeEvent) {
    throw parseAiError({ message: "AI analyse ended unexpectedly. Please try again." });
  }

  return completeEvent;
};
